/**
 * Fixture signing keys — the two branches `GET /ops/v1/keys` answers with, in the browser's copy.
 *
 * Fixture mode never contacts a server, so `createFixtureApi().listKeys()` has to answer with real
 * rows. What makes this a *copy* rather than a second source of truth is
 * `keys-measured-shapes.json`: `tests/test_acs_u16e_keys_door.py` writes it by calling the real door on
 * `MemoryStore` with a temporary secret dir, and `test/keysShapes.test.ts` fails when a row below stops
 * having the measured key set, stops matching the measured `alg`, or moves off the branch the door
 * measured (minted kid → both dates present; a kid this service did not mint → two nulls). So a door
 * that changes and a fixture that drifts are both red tests, not a page that renders a fiction.
 *
 * Two rules this file obeys:
 *  - **No key material, in either direction.** The rows carry the five published fields and nothing
 *    else — no `d`, no `x`, no `kty` (invariant ۴ of `AGENTS.md`, and the reason the public half stays
 *    with `/.well-known/jwks.json`, which this console neither fetches nor proxies).
 *  - **The dates are derived the way the server derives them.** `created_at` is the instant written
 *    into the kid (`acs-<epoch>-<token>`), `not_after` is that instant plus the 90-day discipline —
 *    so a row that says one date and a kid that says another is a bug in this file, and the shape guard
 *    checks the arithmetic against `FIXTURE_KEYS_DISCIPLINE_DAYS`, which the artefact ties to
 *    `acs_service/jwks.py::ROTATION_DAYS`.
 */

import type { SigningKeyMetadata } from '../api/types';

/**
 * `acs_service/jwks.py::ROTATION_DAYS`, mirrored.
 *
 * The page shows this as the *discipline* the door adds to the recorded minting instant; the artefact
 * carries the server's own number and `test/keysShapes.test.ts` refuses to stay green while the two
 * disagree, so the sentence «۹۰ روز» on screen is not a paraphrase someone forgot to update.
 */
export const FIXTURE_KEYS_DISCIPLINE_DAYS = 90;

/** `acs_service/jwks.py::ALG`, mirrored for the same reason and checked the same way. */
export const FIXTURE_KEYS_ALG = 'EdDSA';

/** The prefix `jwks.generate_keypair()` writes into a kid this service minted. */
export const FIXTURE_KEYS_OWNED_PREFIX = 'acs';

/** The ids the artefact measures; a row lives under its branch id, never inside the row. */
export type FixtureKeysBranchId = 'minted-kid' | 'foreign-kid';

/** `jwks._iso`: second precision, UTC, `Z` — the format the door's timestamps arrive in. */
function keyIso(epochSeconds: number): string {
  return new Date(epochSeconds * 1000).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/**
 * A row for a kid this service minted: the two dates are read off the kid exactly the way
 * `jwks.inventory()` reads them, so `kid`, `created_at` and `not_after` cannot disagree.
 */
function mintedRow(epochSeconds: number, token: string, active: boolean): SigningKeyMetadata {
  return {
    kid: `${FIXTURE_KEYS_OWNED_PREFIX}-${epochSeconds}-${token}`,
    alg: FIXTURE_KEYS_ALG,
    created_at: keyIso(epochSeconds),
    not_after: keyIso(epochSeconds + FIXTURE_KEYS_DISCIPLINE_DAYS * 86_400),
    active,
  };
}

const DAY = 86_400;
const NOW_EPOCH = Math.floor(Date.parse('2026-10-17T09:00:00Z') / 1000);

/**
 * The rows, grouped by the branch the door was measured answering.
 *
 * Three rows, and every one of them is a state a real deployment is in today or will be: the key that
 * signs now (minted 12 days ago, so its discipline date is still ahead), a key minted 200 days ago
 * whose discipline date has passed while it keeps working — the case this door exists to make visible
 * rather than hide — and a published kid this service did not mint, whose two dates are nulls because
 * nothing recorded them.
 */
export const FIXTURE_KEYS_ROWS: Record<FixtureKeysBranchId, readonly SigningKeyMetadata[]> = {
  'minted-kid': [
    mintedRow(NOW_EPOCH - 12 * DAY, 'c0ffee01', true),
    mintedRow(NOW_EPOCH - 200 * DAY, 'beef0002', false),
  ],
  'foreign-kid': [
    {
      kid: 'core-legacy-0001',
      alg: FIXTURE_KEYS_ALG,
      created_at: null,
      not_after: null,
      active: false,
    },
  ],
};

/** Every row the fixture ships, in the door's own order (the published JWKS, newest first). */
export function fixtureKeyRows(): SigningKeyMetadata[] {
  return Object.values(FIXTURE_KEYS_ROWS).flatMap((rows) => rows.map((row) => ({ ...row })));
}

/** The branch ids the artefact must describe; an id with no measurement is an invented branch. */
export function fixtureKeyBranchIds(): FixtureKeysBranchId[] {
  return Object.keys(FIXTURE_KEYS_ROWS) as FixtureKeysBranchId[];
}
